import { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';

interface Subscription {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

interface Order {
  customer_id: string;
  order_id: number;
  checkout_session_id: string;
  payment_intent_id: string;
  amount_subtotal: number;
  amount_total: number;
  currency: string;
  payment_status: string;
  order_status: string;
  order_date: string;
}

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [customerCreatedAt, setCustomerCreatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // Changed default to false
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setOrders([]);
      setCustomerCreatedAt(null);
      setLoading(false);
      return;
    }

    fetchSubscriptionData();
  }, [user]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Dynamic import to avoid loading Supabase bundle on homepage
      const { supabase } = await import('../lib/supabase');

      // Check if user exists in stripe_customers table
      const { data: customerData, error: customerError } = await supabase
        .from('stripe_customers')
        .select('customer_id, created_at')
        .eq('user_id', user!.id)
        .is('deleted_at', null)
        .maybeSingle();

      if (customerError) {
        throw customerError;
      }

      if (!customerData) {
        // User doesn't have a customer record, so no subscription or orders
        setSubscription(null);
        setOrders([]);
        setCustomerCreatedAt(null);
        setLoading(false);
        return;
      }

      // Store customer created_at for billing portal routing
      setCustomerCreatedAt(customerData.created_at || null);

      // Fetch subscription data
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('stripe_user_subscriptions')
        .select('*')
        .maybeSingle();

      if (subscriptionError) {
        throw subscriptionError;
      }

      setSubscription(subscriptionData);

      // Fetch orders data (for lifetime purchases)
      // Don't filter here - let hasActiveSubscription() do the filtering
      const { data: ordersData, error: ordersError } = await supabase
        .from('stripe_user_orders')
        .select('*');

      if (ordersError) {
        throw ordersError;
      }

      setOrders(ordersData || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscription data';
      setError(errorMessage);
      console.error('Subscription fetch error:', err);

      // IMPORTANT: Do NOT set subscription to null on error!
      // This would make paying customers appear as free users.
      // Instead, keep the previous state and let the UI show an error/retry option.
      // The error state is already set above, so components can check for it.
    } finally {
      setLoading(false);
    }
  };

  const hasActiveSubscription = () => {
    // Check for active subscription
    if (subscription?.subscription_status === 'active') {
      return true;
    }

    // Check for completed lifetime purchase
    if (orders.length > 0) {
      return orders.some(order =>
        order.order_status === 'completed' &&
        (order.payment_status === 'paid' || order.payment_status === 'no_payment_required')
      );
    }

    return false;
  };

  const isSubscriptionCanceled = () => {
    return subscription?.cancel_at_period_end === true;
  };

  const getCurrentPeriodEnd = () => {
    if (!subscription?.current_period_end) return null;
    return new Date(subscription.current_period_end * 1000);
  };

  const getSubscriptionType = () => {
    // Check for lifetime purchase first
    if (orders.length > 0) {
      const lifetimeOrder = orders.find(order =>
        order.order_status === 'completed' &&
        (order.payment_status === 'paid' || order.payment_status === 'no_payment_required')
      );
      if (lifetimeOrder) {
        return 'lifetime';
      }
    }

    // Check for active subscription
    if (subscription?.subscription_status === 'active') {
      return 'subscription';
    }

    return null;
  };

  return {
    subscription,
    orders,
    customerCreatedAt,
    loading,
    error,
    hasActiveSubscription,
    isSubscriptionCanceled,
    getCurrentPeriodEnd,
    getSubscriptionType,
    refetch: fetchSubscriptionData,
  };
};